import React from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { useTranslation } from 'react-i18next';

const AppleIcon = () => (
  <svg width="28" height="28" viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const WindowsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 448 512" fill="currentColor">
    <path d="M0 93.7l183.6-25.3v177.4H0V93.7zm0 324.6l183.6 25.3V268.4H0v149.9zm203.8 28L448 480V268.4H203.8v177.9zm0-380.6v180.1H448V32L203.8 65.7z"/>
  </svg>
);

const BUILD_DATE = '20260514';

const downloads = [
  { name: `drama-dl-mac-arm64-${BUILD_DATE}`, label: 'macOS (Apple Silicon)', desc: 'macChipAppleSilicon', Icon: AppleIcon, file: '/downloads/drama-dl-mac-arm64' },
  { name: `drama-dl-mac-intel-${BUILD_DATE}`, label: 'macOS (Intel)', desc: 'macChipIntel', Icon: AppleIcon, file: '/downloads/drama-dl-mac-intel' },
  { name: `drama-dl-${BUILD_DATE}.exe`, label: 'Windows', desc: 'winArch', Icon: WindowsIcon, file: '/downloads/drama-dl.exe' },
];

const DownloadTool: React.FC = () => {
  const { t } = useTranslation('profile');
  return (
    <div className="container" style={{ paddingTop: '24px', paddingBottom: '48px' }}>
      <Breadcrumb items={[{ label: t('download.breadcrumbHome'), to: '/projects' }, { label: t('download.breadcrumbTitle') }]} />

      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '16px',
            background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 600 }}>{t('download.title')}</h2>
          <p style={{ margin: 0, color: 'var(--text-3)', fontSize: '14px' }}>
            {t('download.subtitle')}
          </p>
        </div>

        {/* Download Cards */}
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>{t('download.installTitle')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '32px' }}>
          {downloads.map(d => (
            <a
              key={d.name}
              href={d.file}
              download={d.name}
              className="card"
              style={{
                padding: '16px', textAlign: 'center', textDecoration: 'none', color: 'inherit',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                transition: 'border-color var(--transition-fast)',
              }}
            >
              <span style={{ color: 'var(--text-2)' }}><d.Icon /></span>
              <span style={{ fontWeight: 600, fontSize: '13px' }}>{d.label}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{t('download.' + d.desc)}</span>
            </a>
          ))}
        </div>

        {/* Usage */}
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>{t('download.usageTitle')}</h3>
        <div className="card" style={{ padding: '20px', marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 8px' }}>{t('download.interactiveTitle')}</h4>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', margin: '0 0 8px' }}>
              {t('download.interactiveDesc')}
            </p>
            <pre style={{
              background: 'var(--bg-2)', borderRadius: '8px', padding: '12px 16px',
              fontSize: '12px', lineHeight: '1.8', overflow: 'auto', margin: 0,
            }}>{`用户名: your_username
密码: ****
项目ID: 650f27f3-xxxx-xxxx-xxxx
下载目录 [./downloads]: ./my-videos
集范围 (回车=全部, 如 1-5,8): 1-3`}</pre>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 8px' }}>{t('download.cliTitle')}</h4>
            <pre style={{
              background: 'var(--bg-2)', borderRadius: '8px', padding: '12px 16px',
              fontSize: '12px', lineHeight: '1.8', overflow: 'auto', margin: 0,
            }}>{`# 下载全部集
./drama-dl --user=用户名 --pass=密码 --project=项目ID --output=./videos

# 指定集范围
./drama-dl --user=用户名 --pass=密码 --project=项目ID --episodes=1-5,8

# 使用测试环境
./drama-dl --sandbox --user=用户名 --pass=密码 --project=项目ID`}</pre>
          </div>

          <div style={{
            background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)',
            borderRadius: '8px', padding: '16px 20px',
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 12px', color: '#ca8a04' }}>⚠ {t('download.macFirstRun')}</h4>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.7' }}>
              <p style={{ margin: '0 0 4px' }}>{t('download.macFirstRunStep1')}</p>
              <p style={{ margin: '0 0 2px' }}>{t('download.macFirstRunStep2')}</p>
              <pre style={{
                background: 'rgba(0,0,0,0.15)', borderRadius: '6px', padding: '8px 12px',
                fontSize: '12px', margin: '0 0 8px', overflow: 'auto',
              }}>{t('download.macFirstRunStep2Cmd')}</pre>
              <p style={{ margin: '0 0 2px' }}>{t('download.macFirstRunStep3')}</p>
              <pre style={{
                background: 'rgba(0,0,0,0.15)', borderRadius: '6px', padding: '8px 12px',
                fontSize: '12px', margin: '0 0 8px', overflow: 'auto',
              }}>{t('download.macFirstRunStep3Cmd')}</pre>
              <p style={{ margin: '0 0 2px' }}>{t('download.macFirstRunStep4')}</p>
              <pre style={{
                background: 'rgba(0,0,0,0.15)', borderRadius: '6px', padding: '8px 12px',
                fontSize: '12px', margin: '0 0 8px', overflow: 'auto',
              }}>{t('download.macFirstRunStep4Cmd')}</pre>
              <p style={{ margin: '0 0 2px' }}>{t('download.macFirstRunStep5')}</p>
              <pre style={{
                background: 'rgba(0,0,0,0.15)', borderRadius: '6px', padding: '8px 12px',
                fontSize: '12px', margin: '0 0 12px', overflow: 'auto',
              }}>{t('download.macFirstRunStep5Cmd')}</pre>
              <p style={{
                margin: 0, fontSize: '12px', color: '#a16207',
              }}>{t('download.macFirstRunNote')}</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>{t('download.featuresTitle')}</h3>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[
              { title: t('download.featureIncremental'), desc: t('download.featureIncrementalDesc') },
              { title: t('download.featureConcurrent'), desc: t('download.featureConcurrentDesc') },
              { title: t('download.featureArchive'), desc: t('download.featureArchiveDesc') },
              { title: t('download.featureVersion'), desc: t('download.featureVersionDesc') },
            ].map(f => (
              <div key={f.title}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 4px' }}>{f.title}</h4>
                <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: 0, lineHeight: '1.5' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Output Example */}
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '24px 0 12px' }}>{t('download.structureTitle')}</h3>
        <div className="card" style={{ padding: '20px' }}>
          <pre style={{
            background: 'var(--bg-2)', borderRadius: '8px', padding: '12px 16px',
            fontSize: '12px', lineHeight: '1.8', overflow: 'auto', margin: 0,
          }}>{`downloads/
├── .downloaded.json          # 下载记录（勿删除）
├── EP01_开场/
│   ├── shot_001_v2_selected.mp4
│   ├── shot_001_v1_star.mp4
│   ├── shot_002_v1_selected.mp4
│   └── ...
└── EP02_重逢/
    └── ...`}</pre>
        </div>
      </div>
    </div>
  );
};

export default DownloadTool;
